import { type FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { ZIndexLayer } from "shared/lib/styles";

import { getHasArticleSpeech } from "scmp-app/components/article/article-speech/utilities";
import type { articleSpeechInlineWidgetArticle$key } from "scmp-app/queries/__generated__/articleSpeechInlineWidgetArticle.graphql";

import { ArticleSpeechInlineWidgetArticle20 } from "./variants/article-2.0";
import { ArticleSpeechInlineWidgetGeneric } from "./variants/generic";
import { ArticleSpeechInlineWidgetPlusArticle } from "./variants/plus-article";

export type ArticleSpeechInlineWidgetProps = {
  className?: string;
  onClick?: () => void;
  reference: articleSpeechInlineWidgetArticle$key;
  variant: "article-2.0-minify" | "generic" | "generic-minify" | "plus-article";
  withMinifyCleanUpButton?: boolean;
  withMinifyText?: boolean;
  zIndex?: string;
};

export const ArticleSpeechInlineWidget: FunctionComponent<ArticleSpeechInlineWidgetProps> = ({
  className,
  onClick,
  reference,
  variant,
  withMinifyCleanUpButton = true,
  withMinifyText = true,
  zIndex = `${ZIndexLayer.Drawer - 1}`,
}) => {
  const article = useFragment(
    graphql`
      fragment articleSpeechInlineWidgetArticle on Article {
        ...genericArticleSpeechInlineWidgetGenericArticle
        ...utilitiesGetHasArticleSpeechArticle
      }
    `,
    reference,
  );

  const hasSpeech = getHasArticleSpeech(article);

  const handleRenderInlineWidget = () => {
    if (!hasSpeech) {
      return null;
    }

    switch (variant) {
      case "article-2.0-minify":
        return (
          <ArticleSpeechInlineWidgetArticle20
            className={className}
            extendClick={onClick}
            isMinify={true}
            keepMinifyText={true}
            reference={article}
            withMinifyCleanUpButton={withMinifyCleanUpButton}
            withMinifyText={withMinifyText}
            zIndex={zIndex}
          />
        );
      case "generic-minify":
        return (
          <ArticleSpeechInlineWidgetGeneric
            className={className}
            extendClick={onClick}
            isMinify={true}
            reference={article}
            withMinifyText={withMinifyText}
            zIndex={zIndex}
          />
        );
      case "plus-article":
        return (
          <ArticleSpeechInlineWidgetPlusArticle
            className={className}
            extendClick={onClick}
            isMinify={true}
            keepMinifyText={true}
            reference={article}
            withMinifyCleanUpButton={withMinifyCleanUpButton}
            withMinifyText={withMinifyText}
            zIndex={zIndex}
          />
        );
      default:
        return (
          <ArticleSpeechInlineWidgetGeneric
            className={className}
            extendClick={onClick}
            reference={article}
            zIndex={zIndex}
          />
        );
    }
  };
  return handleRenderInlineWidget();
};

ArticleSpeechInlineWidget.displayName = "ArticleSpeechInlineWidget";
